// Relative path: src/components/ReviewBlock.js

import React, { useState, useRef, useEffect, useMemo } from 'react';
import InfoTooltip from './InfoTooltip';
import recUtils from '../utils/RecUtils';
import { getProfilePicture } from '../utils/profilePictureRegistry';
import verifiedIcon from '../images/verified.svg';
import bulletUpvote from '../images/bulletUpvote.svg';
import bulletDownvote from '../images/bulletDownvote.svg';

// Optimized profile picture component using static imports
const ProfilePicture = React.memo(({ reviewerId, reviewerName, authorImg }) => {
  // Use authorImg if available, otherwise get from registry
  const profilePicture = useMemo(() => {
    if (authorImg) {
      return authorImg;
    }

    if (!reviewerId) {
      return getProfilePicture('pp1.webp'); // fallback
    }

    // Get profile picture path from recUtils and convert to static import
    const profilePicturePath = recUtils.getProfilePictureForReviewer(reviewerId);
    return getProfilePicture(profilePicturePath);
  }, [reviewerId, authorImg]);

  return (
    <img
      src={profilePicture}
      alt={reviewerName}
      className="w-10 h-10 md:ml-5 rounded-full object-cover"
    />
  );
});

/**
 * Helper component to render a label bubble.
 * Expects a label object with properties:
 *   - text: string (the label text)
 *   - positive: boolean (true to show a checkmark, false for an X sign)
 */
const LabelBubble = React.memo(({ label }) => {
  const tooltipText = useMemo(() => {
    const labelText = label.text.toLowerCase();

    if (labelText.includes("had prior surgery")) {
      return "This patient had undergone a previous hair transplant procedure before their current treatment.";
    } else if (labelText.includes("no prior surgery")) {
      return "This patient had not undergone any previous hair transplant procedures.";
    } else if (labelText.includes("diffuse thinner")) {
      return "This patient experienced widespread hair thinning across their scalp rather than specific pattern baldness.";
    } else if (labelText.includes("not diffuse thinner")) {
      return "This patient did not have widespread thinning and likely had pattern baldness in specific areas.";
    } else if (labelText.includes("no medication")) {
      return "This patient did not use any hair loss medications like finasteride or minoxidil after their procedure.";
    } else if (labelText.includes("used finasteride")) {
      return "This patient used finasteride (a DHT blocker) as part of their post-procedure treatment plan.";
    } else if (labelText.includes("used minoxidil")) {
      return "This patient used minoxidil (a topical growth stimulant) as part of their post-procedure treatment plan.";
    } else if (labelText.includes("used dutasteride")) {
      return "This patient used dutasteride (a stronger DHT blocker than finasteride) as part of their post-procedure treatment plan.";
    } else {
      return `This label indicates a specific characteristic of the patient's case: ${label.text}`;
    }
  }, [label.text]);

  return (
    <InfoTooltip 
      text={tooltipText}
      position="right"
      maxWidth={300}
      mobileTitle={`About: ${label.text}`}
      icon={
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${label.positive ? 'bg-blue-100 text-gray-500' : 'bg-blue-100 text-gray-500'}`}>
          {label.positive ? (
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span>{label.text}</span>
        </span>
      }
    />
  );
});

/**
 * Star rating component - matches FeaturedReviewCard implementation
 */
const StarRating = React.memo(({ rating = 0, maxRating = 5 }) => {
  // Convert satisfaction level (1-5) to star rating, handle string values
  const numericRating = typeof rating === 'string' ? parseInt(rating, 10) : rating;
  const validRating = isNaN(numericRating) || numericRating < 0 ? 0 : Math.min(numericRating, maxRating);

  const stars = [];
  for (let i = 1; i <= maxRating; i++) {
    stars.push(
      <svg
        key={i}
        className={`w-6 h-6 ${i <= validRating ? 'text-gray-600 fill-current' : 'text-gray-300 fill-current'}`}
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
});

/**
 * Clickable website source component that handles review navigation
 */
const ClickableWebsiteSource = React.memo(({ 
  websiteSource, 
  siteLogo, 
  externalUrl, 
  reviewId, 
  isExternal 
}) => {
  // Handle click to open review URL
  const handleClick = (e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    
    let urlToOpen;
    
    if (isExternal && externalUrl && externalUrl !== '#') {
      // External review - use the provided external URL
      urlToOpen = externalUrl;
    } else {
      // Internal review - construct our website URL
      urlToOpen = `/review/${reviewId}`;
    }
    
    // Open in new tab
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      onClick={handleClick}
      className="text-sm leading-[20px] md:leading-[22px] text-gray-400 hover:text-gray-600 transition-colors duration-200 flex items-center"
    >
      <span className="underline text-xs md:text-base">View source</span>
      <span className="text-gray-600 text-xs md:text-base">&nbsp;{websiteSource}</span>
      {siteLogo && (
        <img
          src={siteLogo}
          alt={`${websiteSource} logo`}
          className="w-4 h-4 md:ml-1 object-contain"
        />
      )}
    </button>
  );
});

/**
 * Unified ReviewBlock component for displaying patient reviews
 * 
 * @param {Object} props
 * @param {string} props.reviewId - Unique review identifier
 * @param {string} props.reviewerName - Name of the reviewer
 * @param {number|string} props.satisfactionLevel - Satisfaction rating (1-5)
 * @param {string} props.externalUrl - Link to original review
 * @param {string} props.websiteSource - Display name of source website
 * @param {string} props.siteLogo - Logo URL/import for source website
 * @param {string} props.authorImg - Optional custom author image URL
 * @param {string} props.operationDate - Formatted operation date
 * @param {Array} props.dynamicLabels - Array of label objects with text and positive properties
 * @param {Array} props.displayedQuotes - Array of quote objects with text and sentiment
 * @param {boolean} props.virtualizeRendering - Whether to use intersection observer for performance
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.highlightText - Optional highlight text for AI commentary
 * @param {boolean} props.isExternal - Whether this review is external or internal
 */
const ReviewBlock = React.memo(({ 
  reviewId,
  reviewerName,
  satisfactionLevel = -1,
  externalUrl = '#',
  websiteSource = 'Unknown Source',
  siteLogo = null,
  authorImg = null,
  operationDate = '',
  dynamicLabels = [],
  displayedQuotes = [],
  virtualizeRendering = false,
  className = '',
  highlightText = null,
  isExternal = true
}) => {
  const [isVisible, setIsVisible] = useState(!virtualizeRendering);
  const blockRef = useRef(null);

  useEffect(() => {
    if (!virtualizeRendering) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px' // Start loading slightly before visible
      }
    );

    if (blockRef.current) {
      observer.observe(blockRef.current);
    }

    return () => observer.disconnect();
  }, [virtualizeRendering]);

  if (virtualizeRendering && !isVisible) {
    return (
      <div 
        ref={blockRef} 
        className={`rounded-lg mb-4 mt-4 md:mx-4 overflow-hidden min-h-[200px] bg-gray-100 flex items-center justify-center ${className}`}
      >
        <div className="text-gray-500">Loading review...</div>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-lg mb-4 mt-6 md:mx-4 overflow-hidden ${className}`}
    >
      <div className="bg-transparent py-3 md:px-3">
        {/* Author image with reviewer name, verified badge, and website source */}
        <div className="flex items-center mt-2">
          <div className="mr-3">
            <ProfilePicture 
              reviewerId={reviewId}
              reviewerName={reviewerName}
              authorImg={authorImg}
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center mb-1">
              <span className="font-semibold leading-[20px] mr-2">{reviewerName}</span>
              <InfoTooltip
                text="This user has been verified by our team. Their review has been confirmed to be authentic based on their procedure documentation and follow-up results."
                position="bottom"
                maxWidth={300}
                mobileTitle="Verified User"
                icon={
                  <img
                    src={verifiedIcon}
                    alt="Verified"
                    className="w-4 h-4 cursor-pointer"
                  />
                }
              />
            </div>
            <ClickableWebsiteSource
              websiteSource={websiteSource}
              siteLogo={siteLogo}
              externalUrl={externalUrl}
              reviewId={reviewId}
              isExternal={isExternal}
            />
          </div>
        </div>
      </div>
      
      {/* Soft divider like FeaturedReviewCard */}
      <div className="border-t border-gray-200 md:mx-7"/>
      
      {/* Rating and Labels Row - matches FeaturedReviewCard layout */}
      <div className="px-0 md:px-8 pt-4 bg-transparent">
        <div className="flex flex-wrap items-center gap-2">
          {/* Star Rating */}
          <StarRating rating={satisfactionLevel} maxRating={5} />
          
          {/* Dynamic Labels */}
          {dynamicLabels.length > 0 && dynamicLabels.map((label, i) => (
            <LabelBubble key={i} label={label} />
          ))}
        </div>
      </div>

      <div className="md:px-2 bg-transparent">
        {displayedQuotes.map((quoteObj, qIdx) => {
          const isPositive = quoteObj.sentiment === 'positive';
          const sentimentEmoji = isPositive ? '👍' : '👎';
          const sentimentTooltipText = isPositive
            ? "This is a positive comment from the patient's review"
            : "This is a negative comment from the patient's review";

          return (
            <div
              key={qIdx}
              className="relative my-4 rounded-xl"
            >
              <div className="flex items-start justify-start md:ml-2 md:px-4">
                
                <div className="flex items-start gap-2 text-gray-700 flex-1 text-[15px] leading-[20px] md:text-[16px] md:leading-[22px] text-sm sm:text-base">
                  <img
                    src={isPositive ? bulletUpvote : bulletDownvote}
                    alt={isPositive ? 'Positive bullet' : 'Negative bullet'}
                    className="w-4 h-4 mt-1 flex-shrink-0"
                  />
                  <p>{quoteObj.text}</p>
                </div>
                
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default ReviewBlock;